import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwConfigService,
  DwConfigServiceToken,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';

import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import { BunnyPlayerTestSkipMode } from '../../models/bunny-player-test-settings.model';
import { VidstackIframePlayerConfig, VidstackIframePlayerEvent } from '../../models/vidstack-iframe-player.model';
import { BunnyPlayerTestSettingsService } from '../../services/bunny-player-test-settings.service';

interface MediaPlayerWebComponent extends HTMLElement {
  autoplay?: boolean;
  currentTime?: number;
  destroy?: () => void;
  duration?: number;
  pause?: () => Promise<void> | void;
  play?: () => Promise<void>;
  seekableEnd?: number;
  textTracks?: {
    add: (track: VidstackTextTrack) => void;
  };
}

interface VidstackTextTrack {
  cues?: { length: number };
  label?: string;
  language?: string;
  mode?: string;
  addEventListener: (eventName: string, listener: (event: CustomEvent) => void) => void;
}

interface MediaVideoLayoutWebComponent extends HTMLElement {
  disableTimeSlider?: boolean;
  noGestures?: boolean;
  playbackRates?: number[];
}

interface VidstackHlsProvider {
  config?: {
    xhrSetup?: (xhr: XMLHttpRequest, url: string) => void;
  };
}

interface VideoPlaybackPrototype {
  VideoKey: string;
  ProviderLibraryId: number;
  PlaybackConfigKey: string;
  StandardPlayerUrl?: string;
  UnsignedHlsUrl?: string;
  ThumbnailPreviewUrl?: string;
  Source: {
    Url: string;
    Format: string;
    ExpiresAtUtc: string;
  };
}

interface PlaybackTestOption {
  key: string;
  label: string;
  player: string;
  format: string;
  description: string;
}

@DwComponent({
  componentType: DwComponentType.pageComponent,
  key: 'bunny-cdn-token-test-page',
  name: 'Bunny CDN Token Test Page',
  isGlobal: true
})
@Component({
  selector: 'app-bunny-cdn-token-test-page',
  templateUrl: './bunny-cdn-token-test-page.component.html',
  styleUrls: ['./bunny-cdn-token-test-page.component.scss']
})
export class BunnyCdnTokenTestPageComponent implements AfterViewInit, OnDestroy {
  private static vidstackLoadPromise: Promise<any>;

  private readonly vidstackTestUrl = 'https://files.vidstack.io/sprite-fight/hls/stream.m3u8';
  private readonly vidstackTestPosterUrl = 'https://files.vidstack.io/sprite-fight/poster.webp';
  private readonly vidstackTestThumbnailPreviewUrl = 'https://files.vidstack.io/sprite-fight/thumbnails.vtt';
  private readonly vidstackTestCaptionUrl = 'https://files.vidstack.io/sprite-fight/subs/english.vtt';
  private readonly bunnyTestCaptionUrl =
    'https://vz-30481734-031.b-cdn.net/b3d2d3a9-a736-44a2-8219-0938c7e8faae/captions/en-auto.vtt';
  private readonly playbackApiUrl: string;

  readonly testOptions: PlaybackTestOption[] = [
    {
      key: 'vidstack-iframe',
      label: 'Vidstack iframe',
      player: 'Vidstack iframe',
      format: 'Public HLS',
      description: 'Runs Vidstack in a same-origin, Zone-free iframe and bridges playback events to Angular.'
    },
    {
      key: 'bunny-cdn-token-iframe',
      label: 'Bunny CDN Token iframe',
      player: 'Vidstack iframe',
      format: 'Signed HLS',
      description: 'Loads the Aviator-signed Bunny HLS source in the Zone-free Vidstack iframe.'
    },
    {
      key: 'vidstack-test',
      label: 'Vidstack test',
      player: 'Vidstack',
      format: 'Public HLS',
      description: 'Known-good public HLS stream. This isolates Vidstack and hls.js from Bunny and Aviator.'
    },
    {
      key: 'bunny-standard',
      label: 'Bunny standard',
      player: 'Bunny embedded player',
      format: 'Bunny play URL',
      description: 'The standard Bunny player URL without Aviator CDN-token signing.'
    },
    {
      key: 'bunny-cdn-token',
      label: 'Bunny CDN Token',
      player: 'Vidstack',
      format: 'Signed HLS',
      description: 'Calls the Aviator API and gives its server-signed Bunny HLS URL to Vidstack.'
    },
    {
      key: 'bunny-hls',
      label: 'Bunny HLS',
      player: 'Vidstack',
      format: 'Unsigned HLS',
      description: 'Loads the Bunny playlist directly without a CDN token to isolate authentication behavior.'
    }
  ];

  @ViewChild('playerHost', { static: true }) playerHost: ElementRef<HTMLDivElement>;

  selectedTest = this.testOptions.find(option => option.key === 'vidstack-iframe') || this.testOptions[0];
  loading = false;
  errorMessage = '';
  playerStatus = 'Waiting to initialize';
  captionStatus = 'Not configured';
  currentSourceUrl = '';
  failedRequestUrl = '';
  tokenExpiresAt?: string;
  canPlay = false;
  playing = false;
  playbackStarted = false;
  preventedSeekCount = 0;
  lastPreventedSeekTime?: number;
  lastLoadedAt?: Date;
  configuredSkipMode: BunnyPlayerTestSkipMode = 'default';
  configuredSkipModeLabel = 'Default';
  testAllowsSeek = false;
  testResumePosition = 30;
  currentPlaybackPosition = 0;
  playbackDuration = 0;
  seekableEnd = 0;
  lastSeekEvent = 'None';
  lastHlsError = '';
  lastHlsRequest = '';
  lastHlsRequestStatus = '';
  showIframePlayer = false;
  iframePlayerConfig?: VidstackIframePlayerConfig;

  private player?: MediaPlayerWebComponent;
  private destroyed = false;
  private loadSequence = 0;
  private viewInitialized = false;
  private settingsSubscription: Subscription;

  constructor(
    @Inject(DwConfigServiceToken) configService: DwConfigService,
    private http: HttpClient,
    private settingsService: BunnyPlayerTestSettingsService,
    private dwSecurityUserService: DwSecurityUserService,
    private ngZone: NgZone
  ) {
    this.playbackApiUrl = `${configService.coreConfig.apiRoot}/aviator-online/videos/prototypes/cdn-token-playback`;
    this.applySkipSettings(this.settingsService.current.skipMode);
    this.settingsSubscription = this.settingsService.settings$
      .pipe(skip(1))
      .subscribe(settings => {
        this.applySkipSettings(settings.skipMode);
        if (this.viewInitialized) {
          this.reloadPlayer();
        }
      });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    setTimeout(() => this.reloadPlayer());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadSequence++;
    this.settingsSubscription?.unsubscribe();
    this.destroyPlayer();
  }

  selectTest(event: Event): void {
    const key = (event.target as HTMLSelectElement).value;
    this.selectedTest = this.testOptions.find(option => option.key === key) || this.testOptions[0];
    this.reloadPlayer();
  }

  updateResumePosition(event: Event): void {
    const requestedPosition = Number((event.target as HTMLInputElement).value);
    this.testResumePosition = Number.isFinite(requestedPosition) ? Math.max(0, requestedPosition) : 0;
    this.reloadPlayer();
  }

  reloadPlayer(): void {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.errorMessage = '';
    this.playerStatus = 'Loading source';
    this.captionStatus = 'Not configured';
    this.currentSourceUrl = '';
    this.failedRequestUrl = '';
    this.tokenExpiresAt = undefined;
    this.canPlay = false;
    this.playing = false;
    this.playbackStarted = false;
    this.preventedSeekCount = 0;
    this.lastPreventedSeekTime = undefined;
    this.currentPlaybackPosition = 0;
    this.playbackDuration = 0;
    this.seekableEnd = 0;
    this.lastSeekEvent = 'None';
    this.lastHlsError = '';
    this.lastHlsRequest = '';
    this.lastHlsRequestStatus = '';
    this.destroyPlayer();

    switch (this.selectedTest.key) {
      case 'vidstack-iframe':
        this.renderIframePlayer(
          this.vidstackTestUrl,
          'Vidstack iframe HLS test',
          sequence,
          this.vidstackTestPosterUrl,
          this.vidstackTestThumbnailPreviewUrl
        );
        break;
      case 'bunny-cdn-token-iframe':
        this.loadBunnyDetails(sequence, playback => {
          this.tokenExpiresAt = playback.Source.ExpiresAtUtc;
          this.renderIframePlayer(
            playback.Source.Url,
            'Bunny CDN-token iframe test',
            sequence,
            this.createBunnyPosterUrl(playback.Source.Url),
            playback.ThumbnailPreviewUrl
          );
        });
        break;
      case 'bunny-standard':
        this.loadBunnyDetails(sequence, playback => {
          const sourceUrl = playback.StandardPlayerUrl
            || `https://iframe.mediadelivery.net/embed/${playback.ProviderLibraryId}/${playback.VideoKey}`;
          this.renderBunnyPlayer(sourceUrl, sequence);
        });
        break;
      case 'bunny-cdn-token':
        this.loadBunnyDetails(sequence, playback => {
          this.tokenExpiresAt = playback.Source.ExpiresAtUtc;
          this.loadVidstackSource(playback.Source.Url, 'Bunny CDN-token HLS test', sequence);
        });
        break;
      case 'bunny-hls':
        this.loadBunnyDetails(sequence, playback => {
          if (!playback.UnsignedHlsUrl) {
            this.handleError(new Error('The API did not return an unsigned Bunny HLS URL.'), sequence);
            return;
          }
          this.loadVidstackSource(playback.UnsignedHlsUrl, 'Bunny unsigned HLS test', sequence);
        });
        break;
      default:
        this.loadVidstackSource(this.vidstackTestUrl, 'Vidstack public HLS test', sequence, this.vidstackTestPosterUrl);
        break;
    }
  }

  private loadBunnyDetails(sequence: number, onLoaded: (playback: VideoPlaybackPrototype) => void): void {
    this.playerStatus = 'Requesting Bunny playback details from Aviator';
    this.http.get<VideoPlaybackPrototype>(this.playbackApiUrl).subscribe(
      playback => {
        if (!this.isCurrentLoad(sequence)) {
          return;
        }

        onLoaded(playback);
      },
      error => this.handleError(error, sequence)
    );
  }

  playTestVideo(event?: Event): void {
    event?.stopPropagation();

    if (!this.player?.play) {
      this.errorMessage = 'Vidstack did not expose its player API.';
      return;
    }

    this.player.play().catch(error => this.handleError(error));
  }

  private loadVidstackSource(sourceUrl: string, title: string, sequence: number, posterUrl?: string): void {
    this.currentSourceUrl = sourceUrl;
    this.playerStatus = 'Loading Vidstack';

    if (this.selectedTest.key === 'vidstack-test') {
      this.ngZone.runOutsideAngular(() => {
        this.loadVidstack()
          .then(vidstack => this.renderBasicVidstackPlayer(sourceUrl, title, sequence, vidstack))
          .catch(error => this.ngZone.run(() => this.handleError(error, sequence)));
      });
      return;
    }

    this.loadVidstack()
      .then(() => this.renderVidstackPlayer(sourceUrl, title, sequence, posterUrl))
      .catch(error => this.handleError(error, sequence));
  }

  private renderBunnyPlayer(sourceUrl: string, sequence: number): void {
    this.currentSourceUrl = sourceUrl;
    this.playerStatus = 'Loading Bunny embedded player';

    const iframe = document.createElement('iframe');
    iframe.title = 'Bunny standard player test';
    iframe.src = sourceUrl;
    iframe.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.addEventListener('load', () => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      this.loading = false;
      this.playerStatus = 'Bunny player loaded';
      this.lastLoadedAt = new Date();
    });
    this.playerHost.nativeElement.appendChild(iframe);
  }

  private renderIframePlayer(
    sourceUrl: string,
    title: string,
    sequence: number,
    posterUrl?: string,
    thumbnailPreviewUrl?: string
  ): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }

    this.currentSourceUrl = sourceUrl;
    this.playerStatus = 'Loading Zone-free Vidstack iframe';
    const source = new URL(sourceUrl);
    const requestQueryParameters: { [key: string]: string } = {};
    ['token', 'token_path', 'expires'].forEach(key => {
      const value = source.searchParams.get(key);
      if (value) {
        requestQueryParameters[key] = value;
      }
    });
    this.iframePlayerConfig = {
      sourceUrl,
      posterUrl,
      thumbnailPreviewUrl,
      title,
      autoplay: false,
      allowSeek: this.testAllowsSeek,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5],
      requestOrigin: source.origin,
      requestQueryParameters,
      textTracks: this.selectedTest.key === 'bunny-cdn-token-iframe'
        ? [{
          src: this.bunnyTestCaptionUrl,
          kind: 'captions',
          label: 'English (Auto)',
          language: 'en',
          type: 'vtt',
          default: true
        }]
        : this.selectedTest.key === 'vidstack-iframe'
          ? [{
            src: this.vidstackTestCaptionUrl,
            kind: 'subtitles',
            label: 'English',
            language: 'en-US',
            type: 'vtt',
            default: true
          }]
          : [],
      resumePosition: this.testResumePosition,
      furthestWatchedTime: this.testResumePosition
    };
    this.showIframePlayer = true;
    this.lastLoadedAt = new Date();
  }

  onIframePlayerEvent(message: VidstackIframePlayerEvent): void {
    if (message.eventType === 'ready') {
      this.loading = false;
      this.canPlay = true;
      this.playerStatus = 'Iframe player ready';
    } else if (message.eventType === 'playing') {
      this.playing = true;
      this.playbackStarted = true;
      this.playerStatus = 'Playing';
    } else if (message.eventType === 'pause') {
      this.playing = false;
      this.playerStatus = 'Paused';
    } else if (message.eventType === 'ended') {
      this.playing = false;
      this.playerStatus = 'Completed';
    } else if (message.eventType === 'caption-tracks-configured') {
      const trackCount = Number(message.data?.count) || 0;
      this.captionStatus = trackCount === 1 ? '1 track configured' : `${trackCount} tracks configured`;
    } else if (message.eventType === 'caption-track-load-start') {
      this.captionStatus = `${message.data?.label || 'Caption track'}: loading`;
    } else if (message.eventType === 'caption-track-mode-change') {
      this.captionStatus = `${message.data?.label || 'Caption track'}: ${message.data?.mode || 'unknown mode'}`;
    } else if (message.eventType === 'caption-track-ready') {
      const cueCount = Number(message.data?.cueCount) || 0;
      this.captionStatus = `${message.data?.label || 'Caption track'}: loaded (${cueCount} cues)`;
    } else if (message.eventType === 'caption-track-error') {
      this.errorMessage = message.data?.message || 'The caption track could not be loaded.';
      this.captionStatus = `${message.data?.label || 'Caption track'}: failed`;
    } else if (message.eventType === 'position') {
      this.currentPlaybackPosition = Number(message.data?.currentTime) || 0;
      this.playbackDuration = Number(message.data?.duration) || 0;
      this.seekableEnd = Number(message.data?.seekableEnd) || 0;
    } else if (message.eventType === 'seeked') {
      this.lastSeekEvent = `Seeked to ${this.currentPlaybackPosition.toFixed(1)}s`;
    } else if (message.eventType === 'error') {
      this.loading = false;
      this.playerStatus = 'Iframe playback failed';
      this.errorMessage = message.data?.message || 'Vidstack iframe playback failed.';
    }
  }

  private loadVidstack(): Promise<any> {
    const versionedCdnRoot = '/assets/video/vidstack-loader.js';
    this.addStyleSheet('vidstack-theme', 'https://cdn.jsdelivr.net/npm/vidstack@1.15.6/player/styles/default/theme.css');
    this.addStyleSheet('vidstack-video-layout', 'https://cdn.jsdelivr.net/npm/vidstack@1.15.6/player/styles/default/layouts/video.css');

    if (!BunnyCdnTokenTestPageComponent.vidstackLoadPromise) {
      const importModule = new Function('url', 'return import(url);') as
        (url: string) => Promise<any>;
      BunnyCdnTokenTestPageComponent.vidstackLoadPromise = importModule(versionedCdnRoot);
    }

    return BunnyCdnTokenTestPageComponent.vidstackLoadPromise;
  }

  private renderVidstackPlayer(
    sourceUrl: string,
    title: string,
    sequence: number,
    posterUrl?: string
  ): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }

    const player = document.createElement('media-player') as MediaPlayerWebComponent;
    player.setAttribute('title', title);
    player.setAttribute('src', sourceUrl);
    player.setAttribute('crossorigin', '');
    player.setAttribute('playsinline', '');
    player.setAttribute('load', 'eager');
    player.setAttribute('poster-load', 'eager');
    player.autoplay = false;
    player.removeAttribute('autoplay');

    const resolvedPosterUrl = posterUrl || this.createBunnyPosterUrl(sourceUrl);
    if (resolvedPosterUrl) {
      player.setAttribute('poster', resolvedPosterUrl);
    }

    this.attachPlayerEvents(player, sourceUrl, sequence);

    const provider = document.createElement('media-provider');
    if (resolvedPosterUrl) {
      const poster = document.createElement('media-poster');
      poster.classList.add('vds-poster');
      poster.setAttribute('src', resolvedPosterUrl);
      poster.setAttribute('alt', `${title} preview`);
      provider.appendChild(poster);
    }

    const layout = document.createElement('media-video-layout') as MediaVideoLayoutWebComponent;
    layout.disableTimeSlider = !this.testAllowsSeek;
    layout.noGestures = !this.testAllowsSeek;
    layout.playbackRates = [0.5, 0.75, 1, 1.25, 1.5];
    player.appendChild(provider);
    player.appendChild(layout);
    this.playerHost.nativeElement.appendChild(player);

    this.player = player;
    this.playerStatus = 'Player created; waiting for provider';
    this.lastLoadedAt = new Date();
  }

  private createBunnyPosterUrl(sourceUrl: string): string | undefined {
    try {
      const source = new URL(sourceUrl);
      if (!source.hostname.endsWith('.b-cdn.net') || !source.pathname.endsWith('/playlist.m3u8')) {
        return undefined;
      }

      source.pathname = source.pathname.replace(/\/playlist\.m3u8$/, '/thumbnail_1.jpg');
      return source.toString();
    } catch {
      return undefined;
    }
  }

  private attachPlayerEvents(player: MediaPlayerWebComponent, sourceUrl: string, sequence: number): void {
    const observeSeekRequest = (event: Event) => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }

      const requestedTime = Number((event as CustomEvent).detail);
      this.lastSeekEvent = Number.isFinite(requestedTime)
        ? `Requested ${requestedTime.toFixed(1)}s`
        : 'Seek requested without a numeric position';

    };
    const preventSeeking = (event: Event) => {
      event.preventDefault();
      if (!this.isCurrentLoad(sequence)) {
        return;
      }

      const requestedTime = Number((event as CustomEvent).detail);
      this.preventedSeekCount++;
      this.lastPreventedSeekTime = Number.isFinite(requestedTime) ? requestedTime : undefined;
      this.playerStatus = this.lastPreventedSeekTime === undefined
        ? 'Seek request prevented'
        : `Seek to ${this.lastPreventedSeekTime.toFixed(1)}s prevented`;
    };
    player.addEventListener('media-seeking-request', observeSeekRequest);
    player.addEventListener('media-seek-request', observeSeekRequest);
    if (!this.testAllowsSeek) {
      player.addEventListener('media-seeking-request', preventSeeking, true);
      player.addEventListener('media-seek-request', preventSeeking, true);
    }

    player.addEventListener('provider-change', event => {
      this.configureAuthenticatedHlsRequests((event as CustomEvent).detail, sourceUrl);
      this.updateStatus(sequence, 'HLS provider selected');
    });
    player.addEventListener('hls-lib-load-start', () => this.updateStatus(sequence, 'Loading hls.js'));
    player.addEventListener('hls-lib-loaded', () => this.updateStatus(sequence, 'hls.js loaded'));
    player.addEventListener('can-play', () => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      this.loading = false;
      this.canPlay = true;
      this.playerStatus = 'Ready to play';
    });
    player.addEventListener('playing', () => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      this.playing = true;
      this.playbackStarted = true;
      this.playerStatus = 'Playing';
    });
    player.addEventListener('pause', () => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      this.playing = false;
      this.playerStatus = 'Paused';
    });
    player.addEventListener('time-update', () => this.updatePlaybackDiagnostics(player, sequence));
    player.addEventListener('duration-change', () => this.updatePlaybackDiagnostics(player, sequence));
    player.addEventListener('seeking', () => {
      this.updatePlaybackDiagnostics(player, sequence);
      this.lastSeekEvent = `Seeking at ${this.currentPlaybackPosition.toFixed(1)}s`;
    });
    player.addEventListener('seeked', () => {
      this.updatePlaybackDiagnostics(player, sequence);
      this.lastSeekEvent = `Seeked to ${this.currentPlaybackPosition.toFixed(1)}s`;
    });
    player.addEventListener('hls-error', event => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      const detail = (event as CustomEvent).detail;
      this.lastHlsError = detail?.details || detail?.type || detail?.message || 'HLS error';
    });
    player.addEventListener('hls-lib-load-error', event => {
      this.handlePlayerError('Vidstack could not load hls.js.', event, sequence);
    });
    player.addEventListener('error', event => {
      this.handlePlayerError('The selected video could not be loaded.', event, sequence);
    });
  }

  private destroyPlayer(): void {
    this.showIframePlayer = false;
    this.iframePlayerConfig = undefined;

    if (this.player?.destroy) {
      this.player.destroy();
    }
    this.player = undefined;

    if (this.playerHost?.nativeElement) {
      this.playerHost.nativeElement.innerHTML = '';
    }
  }

  private renderBasicVidstackPlayer(sourceUrl: string, title: string, sequence: number, vidstack: any): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }

    const player = document.createElement('media-player') as MediaPlayerWebComponent;
    player.setAttribute('title', title);
    player.setAttribute('src', sourceUrl);
    player.setAttribute('crossorigin', '');
    player.setAttribute('playsinline', '');
    player.setAttribute('load', 'eager');

    const provider = document.createElement('media-provider');
    const captionTrack = new vidstack.TextTrack({
      id: 'vidstack-test-caption-en',
      src: this.vidstackTestCaptionUrl,
      kind: 'subtitles',
      label: 'English',
      language: 'en-US',
      type: 'vtt',
      default: true
    }) as VidstackTextTrack;
    this.captionStatus = 'English: configured; waiting for Vidstack';

    captionTrack.addEventListener('load-start', () => {
      if (this.isCurrentLoad(sequence)) {
        this.ngZone.run(() => this.captionStatus = 'English: loading');
      }
    });
    captionTrack.addEventListener('load', () => {
      if (this.isCurrentLoad(sequence)) {
        const cueCount = captionTrack.cues?.length || 0;
        this.ngZone.run(() => this.captionStatus = `English: loaded (${cueCount} cues)`);
      }
    });
    captionTrack.addEventListener('error', event => {
      if (this.isCurrentLoad(sequence)) {
        this.ngZone.run(() => {
          this.captionStatus = 'English: failed';
          this.errorMessage = (event.detail as any)?.message || 'The Vidstack public caption track could not be loaded.';
        });
      }
    });
    captionTrack.addEventListener('mode-change', () => {
      if (this.isCurrentLoad(sequence)) {
        this.ngZone.run(() => this.captionStatus = `English: ${captionTrack.mode || 'unknown mode'}`);
      }
    });

    player.addEventListener('text-track-change', event => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }

      const track = (event as CustomEvent).detail;
      this.ngZone.run(() => {
        this.captionStatus = track
          ? `${track.label || track.language || 'Caption track'}: ${track.mode || 'selected'}`
          : 'Captions off';
      });
    });

    player.addEventListener('can-play', () => {
      if (!this.isCurrentLoad(sequence)) {
        return;
      }
      this.ngZone.run(() => {
        this.loading = false;
        this.playerStatus = 'Ready to play';
      });
    });
    player.addEventListener('error', event => {
      this.ngZone.run(() => this.handlePlayerError('The Vidstack test video could not be loaded.', event, sequence));
    });

    player.appendChild(provider);
    player.appendChild(document.createElement('media-video-layout'));
    this.playerHost.nativeElement.appendChild(player);
    if (!player.textTracks) {
      this.handlePlayerError('Vidstack did not expose its text-track API.', null, sequence);
      return;
    }
    player.textTracks.add(captionTrack);
    captionTrack.mode = 'showing';
    this.player = player;
    this.playerStatus = 'Basic Vidstack player created';
    this.lastLoadedAt = new Date();
  }

  private addStyleSheet(id: string, href: string): void {
    const existingLink = document.getElementById(id) as HTMLLinkElement;
    if (existingLink) {
      if (existingLink.href !== href) {
        existingLink.href = href;
      }
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  private updateStatus(sequence: number, status: string): void {
    if (this.isCurrentLoad(sequence)) {
      this.playerStatus = status;
    }
  }

  private updatePlaybackDiagnostics(player: MediaPlayerWebComponent, sequence: number): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }

    const currentTime = Number(player.currentTime);
    const duration = Number(player.duration);
    const seekableEnd = Number(player.seekableEnd);
    this.currentPlaybackPosition = Number.isFinite(currentTime) ? currentTime : 0;
    this.playbackDuration = Number.isFinite(duration) ? duration : 0;
    this.seekableEnd = Number.isFinite(seekableEnd) ? seekableEnd : 0;
  }

  private applySkipSettings(skipMode: BunnyPlayerTestSkipMode): void {
    const defaultAllowsSeek = this.dwSecurityUserService.hasPermission(AppPermissions.videoCanSeek);
    this.configuredSkipMode = skipMode;
    this.testAllowsSeek = skipMode === 'allow' || (skipMode === 'default' && defaultAllowsSeek);
    this.configuredSkipModeLabel = skipMode === 'default'
      ? `Default (${defaultAllowsSeek ? 'allow skipping' : 'prevent skipping'})`
      : skipMode === 'allow'
        ? 'Allow skipping'
        : skipMode === 'completed' ? 'Completed lessons only (prevented in prototype)' : 'Prevent skipping';
  }

  private configureAuthenticatedHlsRequests(provider: VidstackHlsProvider, sourceUrl: string): void {
    const source = new URL(sourceUrl);
    const token = source.searchParams.get('token');
    const tokenPath = source.searchParams.get('token_path');
    const expires = source.searchParams.get('expires');

    if (!provider || !token || !tokenPath || !expires) {
      return;
    }

    provider.config = {
      ...(provider.config || {}),
      xhrSetup: (xhr: XMLHttpRequest, url: string) => {
        const request = new URL(url, sourceUrl);
        if (request.origin === source.origin) {
          request.searchParams.set('token', token);
          request.searchParams.set('token_path', tokenPath);
          request.searchParams.set('expires', expires);
        }
        const requestUrl = request.toString();
        this.lastHlsRequest = this.describeHlsRequest(request);
        this.lastHlsRequestStatus = 'Pending';
        xhr.addEventListener('loadend', () => {
          this.lastHlsRequestStatus = `${xhr.status || 0} ${xhr.statusText || ''}`.trim();
          if (xhr.status >= 400) {
            this.failedRequestUrl = requestUrl;
          }
        });
        xhr.open('GET', requestUrl, true);
      }
    };
  }

  private describeHlsRequest(request: URL): string {
    const fileName = request.pathname.split('/').filter(part => !!part).pop() || request.pathname;
    return `${fileName} (${request.pathname.endsWith('.m3u8') ? 'playlist' : request.pathname.endsWith('.key')
      ? 'key' : 'media'})`;
  }

  private isCurrentLoad(sequence: number): boolean {
    return !this.destroyed && sequence === this.loadSequence;
  }

  private handleError(error: any, sequence: number = this.loadSequence): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }
    this.loading = false;
    this.playerStatus = 'Player initialization failed';
    this.errorMessage = error?.error?.detail || error?.message || 'Unable to load the selected playback test.';
  }

  private handlePlayerError(message: string, event: Event, sequence: number): void {
    if (!this.isCurrentLoad(sequence)) {
      return;
    }
    this.loading = false;
    this.playerStatus = 'Playback failed';
    const detail = (event as CustomEvent).detail;
    this.failedRequestUrl = detail?.context?.url
      || detail?.frag?.decryptdata?.uri
      || detail?.response?.url
      || detail?.networkDetails?.responseURL
      || '';
    const detailMessage = detail?.message || detail?.error?.message || detail?.data?.message;
    const detailCode = detail?.code || detail?.error?.code || detail?.data?.code;
    const diagnostic = [detailCode ? `Code: ${detailCode}.` : '', detailMessage || ''].filter(value => !!value).join(' ');
    this.errorMessage = diagnostic ? `${message} ${diagnostic}` : message;
  }
}
